import { defineMessages, plural } from "../i18n/core";

export const restoreMessages = defineMessages({
  "pt-BR": {
    steps: ["Ponto de restauração", "Arquivos", "Destino", "Revisão"],
    stepsLabel: "Etapas",
    eyebrow: "Restauração",
    lede: "Escolha um ponto no tempo, marque o que restaurar e para onde enviar.",
    backToMachine: "Voltar à máquina",
    mockZip: (n: number) => `Simulação: um ZIP com ${n} ${plural(n, "item", "itens")} seria baixado.`,
    downloadStarted: "Download iniciado. Arquivos grandes podem levar alguns minutos para começar.",
    restoreSent: "Restauração enviada ao agente. Acompanhe o progresso em Atividade.",
    restoreFailed: "Não foi possível iniciar a restauração.",
    targetDownload: "Download pela console (ZIP)",
    targetPath: (host: string, path: string) => `${host} · ${path}`,
    targetOriginal: (host: string) => `${host} · local original`,
    conflictLabel: {
      keep_both: "manter os dois (restaura numa pasta nova ao lado, chamada “E-BACKUP restaurado AAAA-MM-DD HHMM”)",
      skip: "pular arquivos que já existem",
      overwrite: "sobrescrever arquivos existentes",
    },
    whenTitle: "Qual ponto de restauração?",
    whenSub: (n: number) => `${n} ${plural(n, "disponível", "disponíveis")}, do mais recente ao mais antigo`,
    noSnapshots: "Esta máquina ainda não tem pontos de restauração.",
    when: (date: string, time: string) => `${date}, ${time}`,
    partial: "parcial",
    immutableUntil: (date: string) => `imutável até ${date}`,
    whatTitle: "O que restaurar?",
    whatSub: (date: string, n: number) => `${date} · ${n} ${plural(n, "item marcado", "itens marcados")}`,
    clearSelection: "Limpar seleção",
    searchLabel: "Buscar arquivo em todos os pontos",
    searchPlaceholder: "Nome ou parte do nome",
    searchButton: "Buscar",
    closeSearch: "Fechar busca",
    searchHint: "Procura pelo nome em todos os pontos já catalogados desta máquina. Um ponto novo entra no catálogo minutos depois do backup.",
    searchResults: (n: number) => `${n} ${plural(n, "item encontrado", "itens encontrados")}`,
    searchLimit: "A busca mostra até 100; refine o nome se o que procura não apareceu.",
    searchNone: "Nada com esse nome nos pontos catalogados.",
    occurrencesTitle: "Está nestes pontos:",
    occurrencesNone: "Este item não está em nenhum ponto vigente.",
    catalogPending: "Este ponto ainda está sendo catalogado: a navegação abre o repositório e pode demorar mais.",
    catalogFailed: "O catálogo deste ponto não pôde ser montado; a navegação abre o repositório.",
    emptyFolder: "Pasta vazia.",
    select: (name: string) => `Marcar ${name}`,
    folder: "pasta",
    whereTitle: "Para onde?",
    targetGroup: "Destino",
    thisMachine: "Nesta máquina",
    thisMachineHint: (host: string) => `O agente de ${host} restaura no local original ou numa pasta escolhida.`,
    zip: "Baixar como ZIP",
    zipHint: "O servidor monta o arquivo e o navegador baixa. Não precisa do agente online.",
    otherMachine: "Outra máquina",
    otherMachineHint: "Os arquivos seguem pelo servidor até outra máquina desta organização.",
    destinationMachine: "Máquina de destino",
    chooseMachine: "Escolha a máquina",
    offline: "desconectada",
    otherMachineSecurity: "O servidor lê o backup e envia só os itens escolhidos; a máquina de destino não recebe a chave dos backups da origem.",
    otherMachineFolderHint: "Caminho absoluto na máquina de destino. As pastas dos itens são recriadas dentro dele.",
    targetFolder: "Pasta de destino (opcional)",
    targetFolderPlaceholder: (path: string) => `Vazio: ${path}`,
    originalLocation: "local original",
    targetFolderHint: (path: string) => `Caminho absoluto na máquina. Os itens mantêm a estrutura de pastas a partir de ${path}.`,
    ifExists: "Se o arquivo já existir no destino",
    keepBoth: "Manter os dois",
    skip: "Pular",
    overwrite: "Sobrescrever",
    reviewTitle: "Confira antes de iniciar",
    reviewPoint: "Ponto de restauração",
    reviewItems: "Itens",
    reviewTarget: "Destino",
    reviewConflicts: "Conflitos",
    overwriteWarning: "Arquivos existentes no destino serão substituídos. O backup desta máquina continua intacto.",
    offlineWarning: (host: string) => `${host} está offline. A restauração começa assim que o agente se conectar.`,
    continue: "Continuar",
    downloadZip: "Baixar ZIP",
    startRestore: "Iniciar restauração",
  },
  "en-US": {
    steps: ["Restore point", "Files", "Destination", "Review"],
    stepsLabel: "Steps",
    eyebrow: "Restore",
    lede: "Pick a point in time, select what to restore and where to send it.",
    backToMachine: "Back to machine",
    mockZip: (n: number) => `Simulation: a ZIP with ${n} ${plural(n, "item", "items")} would be downloaded.`,
    downloadStarted: "Download started. Large files may take a few minutes to begin.",
    restoreSent: "Restore sent to the agent. Follow the progress in Activity.",
    restoreFailed: "Could not start the restore.",
    targetDownload: "Download from the console (ZIP)",
    targetPath: (host: string, path: string) => `${host} · ${path}`,
    targetOriginal: (host: string) => `${host} · original location`,
    conflictLabel: {
      keep_both: "keep both (restores into a new folder alongside, named “E-BACKUP restored YYYY-MM-DD HHMM”)",
      skip: "skip files that already exist",
      overwrite: "overwrite existing files",
    },
    whenTitle: "Which restore point?",
    whenSub: (n: number) => `${n} available, newest to oldest`,
    noSnapshots: "This machine has no restore points yet.",
    when: (date: string, time: string) => `${date}, ${time}`,
    partial: "partial",
    immutableUntil: (date: string) => `immutable until ${date}`,
    whatTitle: "What to restore?",
    whatSub: (date: string, n: number) => `${date} · ${n} ${plural(n, "item selected", "items selected")}`,
    clearSelection: "Clear selection",
    searchLabel: "Search for a file across all points",
    searchPlaceholder: "Name or part of the name",
    searchButton: "Search",
    closeSearch: "Close search",
    searchHint: "Searches by name across every cataloged restore point of this machine. A new point joins the catalog minutes after the backup.",
    searchResults: (n: number) => `${n} ${plural(n, "item found", "items found")}`,
    searchLimit: "The search shows up to 100; narrow the name if what you need is missing.",
    searchNone: "Nothing with that name in the cataloged points.",
    occurrencesTitle: "Present in these points:",
    occurrencesNone: "This item is not in any current point.",
    catalogPending: "This point is still being cataloged: browsing opens the repository and may take longer.",
    catalogFailed: "The catalog for this point could not be built; browsing opens the repository.",
    emptyFolder: "Empty folder.",
    select: (name: string) => `Select ${name}`,
    folder: "folder",
    whereTitle: "Where to?",
    targetGroup: "Destination",
    thisMachine: "On this machine",
    thisMachineHint: (host: string) => `The agent on ${host} restores to the original location or to a folder you choose.`,
    zip: "Download as ZIP",
    zipHint: "The server builds the file and your browser downloads it. The agent does not need to be online.",
    otherMachine: "Another machine",
    otherMachineHint: "Files travel through the server to another machine in this organization.",
    destinationMachine: "Destination machine",
    chooseMachine: "Choose the machine",
    offline: "offline",
    otherMachineSecurity: "The server reads the backup and sends only the chosen items; the destination machine never receives the key to the source's backups.",
    otherMachineFolderHint: "Absolute path on the destination machine. The items' folders are recreated inside it.",
    targetFolder: "Destination folder (optional)",
    targetFolderPlaceholder: (path: string) => `Empty: ${path}`,
    originalLocation: "original location",
    targetFolderHint: (path: string) => `Absolute path on the machine. Items keep their folder structure starting from ${path}.`,
    ifExists: "If the file already exists at the destination",
    keepBoth: "Keep both",
    skip: "Skip",
    overwrite: "Overwrite",
    reviewTitle: "Review before starting",
    reviewPoint: "Restore point",
    reviewItems: "Items",
    reviewTarget: "Destination",
    reviewConflicts: "Conflicts",
    overwriteWarning: "Existing files at the destination will be replaced. This machine's backup stays intact.",
    offlineWarning: (host: string) => `${host} is offline. The restore starts as soon as the agent connects.`,
    continue: "Continue",
    downloadZip: "Download ZIP",
    startRestore: "Start restore",
  },
  es: {
    steps: ["Punto de restauración", "Archivos", "Destino", "Revisión"],
    stepsLabel: "Pasos",
    eyebrow: "Restauración",
    lede: "Elige un punto en el tiempo, marca qué restaurar y a dónde enviarlo.",
    backToMachine: "Volver al equipo",
    mockZip: (n: number) => `Simulación: se descargaría un ZIP con ${n} ${plural(n, "elemento", "elementos")}.`,
    downloadStarted: "Descarga iniciada. Los archivos grandes pueden tardar unos minutos en comenzar.",
    restoreSent: "Restauración enviada al agente. Sigue el progreso en Actividad.",
    restoreFailed: "No se pudo iniciar la restauración.",
    targetDownload: "Descarga desde la consola (ZIP)",
    targetPath: (host: string, path: string) => `${host} · ${path}`,
    targetOriginal: (host: string) => `${host} · ubicación original`,
    conflictLabel: {
      keep_both: "conservar ambos (restaura en una carpeta nueva al lado, llamada “E-BACKUP restaurado AAAA-MM-DD HHMM”)",
      skip: "omitir archivos que ya existen",
      overwrite: "sobrescribir archivos existentes",
    },
    whenTitle: "¿Qué punto de restauración?",
    whenSub: (n: number) => `${n} ${plural(n, "disponible", "disponibles")}, del más reciente al más antiguo`,
    noSnapshots: "Este equipo todavía no tiene puntos de restauración.",
    when: (date: string, time: string) => `${date}, ${time}`,
    partial: "parcial",
    immutableUntil: (date: string) => `inmutable hasta ${date}`,
    whatTitle: "¿Qué restaurar?",
    whatSub: (date: string, n: number) => `${date} · ${n} ${plural(n, "elemento marcado", "elementos marcados")}`,
    clearSelection: "Borrar selección",
    searchLabel: "Buscar archivo en todos los puntos",
    searchPlaceholder: "Nombre o parte del nombre",
    searchButton: "Buscar",
    closeSearch: "Cerrar búsqueda",
    searchHint: "Busca por nombre en todos los puntos ya catalogados de este equipo. Un punto nuevo entra al catálogo minutos después de la copia.",
    searchResults: (n: number) => `${n} ${plural(n, "elemento encontrado", "elementos encontrados")}`,
    searchLimit: "La búsqueda muestra hasta 100; acota el nombre si lo que buscas no apareció.",
    searchNone: "Nada con ese nombre en los puntos catalogados.",
    occurrencesTitle: "Está en estos puntos:",
    occurrencesNone: "Este elemento no está en ningún punto vigente.",
    catalogPending: "Este punto todavía se está catalogando: la navegación abre el repositorio y puede tardar más.",
    catalogFailed: "No se pudo armar el catálogo de este punto; la navegación abre el repositorio.",
    emptyFolder: "Carpeta vacía.",
    select: (name: string) => `Marcar ${name}`,
    folder: "carpeta",
    whereTitle: "¿A dónde?",
    targetGroup: "Destino",
    thisMachine: "En este equipo",
    thisMachineHint: (host: string) => `El agente de ${host} restaura en la ubicación original o en una carpeta que elijas.`,
    zip: "Descargar como ZIP",
    zipHint: "El servidor arma el archivo y el navegador lo descarga. No hace falta que el agente esté en línea.",
    otherMachine: "Otro equipo",
    otherMachineHint: "Los archivos pasan por el servidor hasta otro equipo de esta organización.",
    destinationMachine: "Equipo de destino",
    chooseMachine: "Elige el equipo",
    offline: "desconectado",
    otherMachineSecurity: "El servidor lee la copia de seguridad y envía solo los elementos elegidos; el equipo de destino nunca recibe la clave de las copias del origen.",
    otherMachineFolderHint: "Ruta absoluta en el equipo de destino. Las carpetas de los elementos se recrean dentro de ella.",
    targetFolder: "Carpeta de destino (opcional)",
    targetFolderPlaceholder: (path: string) => `Vacío: ${path}`,
    originalLocation: "ubicación original",
    targetFolderHint: (path: string) => `Ruta absoluta en el equipo. Los elementos conservan la estructura de carpetas a partir de ${path}.`,
    ifExists: "Si el archivo ya existe en el destino",
    keepBoth: "Conservar ambos",
    skip: "Omitir",
    overwrite: "Sobrescribir",
    reviewTitle: "Revisa antes de iniciar",
    reviewPoint: "Punto de restauración",
    reviewItems: "Elementos",
    reviewTarget: "Destino",
    reviewConflicts: "Conflictos",
    overwriteWarning: "Los archivos existentes en el destino se reemplazarán. La copia de seguridad de este equipo sigue intacta.",
    offlineWarning: (host: string) => `${host} está sin conexión. La restauración comienza en cuanto el agente se conecte.`,
    continue: "Continuar",
    downloadZip: "Descargar ZIP",
    startRestore: "Iniciar restauración",
  },
});
